import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { submitChatLead } from '../services/chatLeadService';
import { getStoredUser } from '../utils/authUser';
import { isLeadPopupEligible, LEAD_POPUP_DELAY_MS } from '../utils/leadCapturePopup';
import './LeadCapturePopup.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

const FEATURE_FLAG_URL = 'https://tophamtechbe.com/api/treas/idx/v1/feature-flag';
const API_KEY = 'treas-api-key-2025';
const DISMISSED_KEY = `leadPopupDismissed_${SITE_CONFIG.SITE_ID}`;
const EXCLUDED_PATH_PREFIXES = [
  '/admin',
  '/password-reset',
  '/privacy-policy',
  '/terms-of-service',
  '/api-docs',
  '/sitemap.xml'
];

const isSignedIn = () => Boolean(getStoredUser()?.token);

const LeadCapturePopup = () => {
  const location = useLocation();
  const [isEnabled, setIsEnabled] = useState(false);
  const [delayComplete, setDelayComplete] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === 'true'
  );
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isExcludedPath = useMemo(
    () => EXCLUDED_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)),
    [location.pathname]
  );
  const shouldShow = isLeadPopupEligible({
    enabled: isEnabled,
    delayComplete,
    dismissed,
    excludedPath: isExcludedPath,
    signedIn: isSignedIn()
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadFeatureFlag = async () => {
      try {
        const params = new URLSearchParams({ siteId: SITE_CONFIG.SITE_ID });
        const response = await fetch(`${FEATURE_FLAG_URL}?${params}`, {
          headers: { 'X-API-Key': API_KEY },
          signal: controller.signal
        });
        if (!response.ok) return;

        const flags = await response.json();
        setIsEnabled(flags?.leadPopUp === true);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          console.error('Error fetching lead pop-up setting:', requestError);
        }
      }
    };

    loadFeatureFlag();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDelayComplete(true), LEAD_POPUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldShow) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        sessionStorage.setItem(DISMISSED_KEY, 'true');
        setDismissed(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldShow]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const updateField = (field) => (event) => {
    setFormData((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isHoneypotSubmission(event.currentTarget)) {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await submitChatLead({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        postalCode: formData.postalCode.trim(),
        smsConsent: formData.smsConsent ? 'true' : 'false',
        serviceReq: 'market insights',
        siteId: SITE_CONFIG.SITE_ID
      }, { rememberContact: true });

      if (!response.ok) throw new Error('Lead request failed');
      sessionStorage.setItem(DISMISSED_KEY, 'true');
      setSubmitted(true);
    } catch (submitError) {
      console.error('Error submitting market insights request:', submitError);
      setError('We could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <aside className="lead-capture-backdrop" aria-label="Optional market updates">
      <section
        className="lead-capture-modal"
        role="dialog"
        aria-modal="false"
        aria-labelledby="lead-capture-title"
      >
        <button
          type="button"
          className="lead-capture-close"
          onClick={dismiss}
          aria-label="Close market insights form"
        >
          ×
        </button>

        {submitted ? (
          <div className="lead-capture-success" role="status">
            <span className="lead-capture-success__icon" aria-hidden="true">✓</span>
            <span className="lead-capture-eyebrow">You’re all set</span>
            <h2 id="lead-capture-title">Watch your inbox</h2>
            <p>We’ll send helpful local market insights your way.</p>
            <button type="button" className="lead-capture-submit" onClick={dismiss}>Done</button>
          </div>
        ) : (
          <>
            <span className="lead-capture-eyebrow">Complimentary local updates</span>
            <h2 id="lead-capture-title">Get free market insights emailed to you—no cost!</h2>
            <p className="lead-capture-intro">
              Tell us a little about what you’re looking for and {SITE_CONFIG.AGENT.NAME} will keep you informed.
            </p>

            <form className="lead-capture-form" onSubmit={handleSubmit}>
          <HoneypotField />
              <label>
                <span>Name</span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={updateField('name')}
                  autoComplete="name"
                  placeholder="Your name"
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={formData.email}
                  onChange={updateField('email')}
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                />
              </label>
              <div className="lead-capture-form__row">
                <label>
                  <span>Phone</span>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={updateField('phoneNumber')}
                    autoComplete="tel"
                    placeholder="Phone number"
                    required
                  />
                </label>
                <label>
                  <span>ZIP code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.postalCode}
                    onChange={updateField('postalCode')}
                    autoComplete="postal-code"
                    placeholder="ZIP code"
                    required
                  />
                </label>
              </div>

              <label className="lead-capture-consent">
                <input type="checkbox" checked={formData.smsConsent} onChange={(event) => setFormData((current) => ({ ...current, smsConsent: event.target.checked }))} />
                <span>Send me text updates too. Consent is optional; message and data rates may apply. Reply STOP to opt out.</span>
              </label>

              {error && <p className="lead-capture-error" role="alert">{error}</p>}
              <button type="submit" className="lead-capture-submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send me market insights'}
              </button>
              <p className="lead-capture-fine-print">
                No cost, no obligation. You can unsubscribe from emails at any time.
              </p>
              <button type="button" className="lead-capture-not-now" onClick={dismiss}>Not now—continue browsing</button>
            </form>
          </>
        )}
      </section>
    </aside>
  );
};

export default LeadCapturePopup;
